/**
 * proactiveMinistryEngine.js
 *
 * Translates the live district state into a natural-language pastoral context
 * string injected as [proactive: ...] in the LLM prompt.
 *
 * Distinct from [panorama: ...] (counts) and [synthesis: ...] (per-message fusion):
 * [proactive: ...] carries PASTORAL INTERPRETATION and SUGGESTIONS that persist
 * across the conversation regardless of what the user asked.
 *
 * The LLM uses this to be proactively pastoral — naturally weaving in context
 * ("A propósito, hay 5 personas que podrían necesitar seguimiento esta semana")
 * even when answering unrelated questions.
 */

import { getDistrictState, getMinistryPressure } from "./districtStateEngine";

const MAX_PROACTIVE_CHARS = 350;

function buildNarrative(districtState) {
  const { insights, priorityReport } = districtState;
  const { pressure, topRecommendation } = priorityReport;

  if (!insights?.length) return null;

  const parts = [];

  // High-severity insights come first (max 2)
  const highInsights = insights.filter((i) => i.severity === "high").slice(0, 2);
  for (const ins of highInsights) {
    parts.push(ins.insight);
  }

  // Fall through to medium when no high signals
  if (parts.length === 0) {
    const medium = insights.find((i) => i.severity === "medium");
    if (medium) parts.push(medium.insight);
  }

  if (parts.length === 0) return null;

  // Add top recommendation when pressure warrants it
  if (topRecommendation && pressure !== "low") {
    parts.push(topRecommendation);
  }

  return parts.join(" ").trim();
}

/**
 * Get the current proactive ministry context for prompt injection.
 * Returns null if district state is empty or all signals are low-priority.
 *
 * @returns {string|null}  "[proactive: ...]" or null
 */
export function getProactiveContext() {
  const state = getDistrictState();
  if (!state) return null;

  const narrative = buildNarrative(state);
  if (!narrative) return null;

  return `[proactive: ${narrative.slice(0, MAX_PROACTIVE_CHARS)}]`;
}

/**
 * Returns true when there are high-priority items in the district state.
 * Use to gate proactive injection on calm sessions.
 *
 * @returns {boolean}
 */
export function hasHighPriorityItems() {
  const state = getDistrictState();
  return state?.insights?.some((i) => i.severity === "high") ?? false;
}

/**
 * Returns the ministry pressure level from district state.
 * Convenience wrapper for components that don't need to import districtStateEngine.
 *
 * @returns {"low"|"medium"|"high"|"critical"|null}
 */
export function getMinistryPressureLevel() {
  return getMinistryPressure();
}
